import NIMType from '@yxim/nim-web-sdk/dist/types/nim/index'
import type { NIMMessage } from '@yxim/nim-web-sdk/dist/types/nim/MessageInterface'
import type { NIMSession } from '@yxim/nim-web-sdk/dist/types/nim/SessionInterface'
import type { NIMUserNameCard } from '@yxim/nim-web-sdk/dist/types/nim/UserInterface'
import type { myMsgType } from '@/types/viewsType/chatType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
import {
  apiDeductionSend,
  apiTranslate,
  apiGetFreeMsgCount,
  apiGetStateList
} from '@/api/chat'
import type {
  freeMsgCountInterface,
  userStateListInterface
} from '@/types/apiType/chatType'

export const useChatStore = defineStore({
  id: 'chat',
  state: () => ({
    nim: {} as NIMType, //nim实例
    sessionList: [] as NIMSession[], //会话列表
    sessionMap: {} as Record<string, NIMSession>, //会话列表{p2p-id:{}}
    currSessionMsgs: [] as myMsgType[], //当前会话消息
    currSessionLastMsg: {} as NIMMessage, //当前会话最后一条消息
    msgs: {}, //消息{用户id:[]}
    currSessionId: '', //当前会话id
    myInfo: {} as NIMUserNameCard, //我的信息
    userInfoList: [] as NIMUserNameCard[], //用户信息数组
    userInfos: {} as Record<string, NIMUserNameCard>, //用户信息
    finished: false, //历史记录加载完了
    limit: 20, //一次20条消息
    allUnread: 0, //会话总未读数
    showSkeleton: true, // 是否显示骨架屏 收到会话列表同步完成后 骨架屏取消
    latestSessionListedUid: '' as string, // 会话列表已有的UID的最新信息UID，给消息列表查询用户状态使用，查询完了就重置为 ''，这样利于监听
    freeMsgCount: {} as freeMsgCountInterface, // 会话限制，会话剩余免费次数，限制等级 10，是否发送图片
    translateTexts: {} as { [prop: string]: string }, //翻译的聊天信息{ idClient: '翻译的文本'}  idClient是IM会话的唯一ID
    IM_SERVER_ID: useRuntimeConfig().public.imServerId, // 服务号ID
    isFirstClickRechargePopup: true, // 是否是第一次点击充值提示弹窗
    /** 用户列表状态 */
    userListStatus: {} as Record<string, userStateListInterface>,
    /** 查询用户列表状态，有定时器3分钟清理一次缓存 */
    queryedUserListStatus: {} as Record<string, string>
  }),
  getters: {
    // 当前会话uid
    currSessionUid(state) {
      return state.currSessionId.replace('p2p-', '')
    }
  },
  actions: {
    /** 获取用户列表状态 */
    async getUserListStatus(uids: (string | number)[], isForce?: boolean) {
      const userStore = useUserStore()
      const runtimeConfig = useRuntimeConfig()
      if (uids.length === 0) return
      // 过滤掉已查询过的
      let uidList: (string | number)[] = []
      if (!isForce) {
        // 过滤获取
        uidList = uids.filter((v) => {
          const isInquire =
            !this.queryedUserListStatus[v] &&
            v !== runtimeConfig.public.imServerId // 已查询过的就忽略 || 忽略小秘书
          if (isInquire) {
            this.queryedUserListStatus[v] = v.toString()
          }
          return isInquire
        })
      } else {
        // 强制获取
        uidList = uids.filter((v) => {
          return v !== runtimeConfig.public.imServerId // 忽略小秘书
        })
      }

      if (uidList.length === 0) return
      await apiGetStateList({ uidList: uidList.toString() }).then((res) => {
        if (res.code === 200) {
          const { data } = res
          data.forEach((v) => {
            this.setUserListStatus(v)
            // 同步用户状态到用户信息里，避免用户信息里没有状态的情况
            userStore.publicSetUserInfo({
              uid: Number(v.uid),
              userState: v.state
            } as userInfoStorageMapInterface)
          })
        }
      })
    },
    /** 设置查询用户列表状态 */
    setQueryedUserListStatus(obj: Record<string, string>) {
      this.queryedUserListStatus = obj
    },
    /** 设置用户列表状态 */
    setUserListStatus(item: userStateListInterface) {
      this.userListStatus[item.uid] = item
    },
    // 重置翻译聊天字段，切换语言时调用
    resetTranslateTexts() {
      this.translateTexts = {}
    },
    // 更新翻译聊天信息，如果缓存里已有就return
    async updateTranslateTexts(idClient: string, text: string) {
      const res = await apiTranslate({ text })
      const { code, data } = res
      if (code === 200) {
        this.translateTexts[idClient] = data || ''
      }
    },
    // 获取会话限制消息
    async getFreeMsgCount(uid: string) {
      const res = await apiGetFreeMsgCount({ tuid: this.currSessionUid || uid })
      const { code, data } = res
      if (code === 200) {
        this.freeMsgCount = data
      }
    },
    // 会话列表已有的UID的最新信息UID，给消息列表查询用户状态使用，查询完了就重置为 ''，这样利于监听
    resetLatestSessionListUid() {
      this.latestSessionListedUid = ''
    },
    /***
     * 跳转会话页面必现调这个方法，用这个方法来判断是否充值；
     * 如果是是服务号的就不做充值判断，如果本机是女用户也正常私聊
     * query: {sessionId:ID} 要传的参数对象
     * */
    toChatPage(query: { [prop: string]: string | number }) {
      const router = useRouter()
      const userStore = useUserStore()
      const publicStore = usePublicStore()
      // 如果已充值
      if (
        userStore.userInfo.isFirstCharge ||
        userStore.userInfo.gender === 2 ||
        String(query.sessionId).replace(/^p2p-/, '') === this.IM_SERVER_ID
      ) {
        router.push({
          path: '/chatPage',
          query,
          replace: router.currentRoute.value.path.indexOf('/room/call') !== -1 // 如果当前页面是在视频通话界面的就先关闭当前页面再跳转
        })
      } else {
        // 未充值
        publicStore.setRechargeTipsPopup(true, 2)
      }
    },
    // 初始化nim
    initNIM() {
      const login = JSON.parse(localStorage.getItem('loginInfo') as string)
      const { netAppKey, netEaseToken, uid } = login
      const NIM = window.NIM
      this.nim = NIM.getInstance({
        debug: false, // 是否开启日志，将其打印到console。集成开发阶段建议打开。
        appKey: netAppKey,
        account: uid, //用于登录IM
        token: netEaseToken, //用于登录IM
        db: true, //若不要开启数据库请设置false。SDK 默认为true。
        syncSessionUnread: true, //同步会话的未读数,多端同步
        onconnect: this.onConnect,
        onwillreconnect: this.onWillReconnect,
        ondisconnect: this.onDisconnect,
        onerror: this.onError,
        //消息收发的监听事件
        // onroamingmsgs: this.onRoamingMsgs,
        // onofflinemsgs: this.onOfflineMsgs,
        onmsg: this.onMsg,
        // 最近会话
        onsessions: this.onSessions,
        onupdatesession: this.onUpdateSession,
        // 用户名片
        onmyinfo: this.onMyInfo,
        onupdatemyinfo: this.onMyInfo,
        onusers: this.onUsers,
        onupdateuser: this.onUpdateUser,
        onsyncdone: this.onSyncdone
      })
    },
    // 连接成功
    onConnect() {
      console.log('IM => 连接成功')
    },
    // 重连
    onWillReconnect(obj) {
      // 此时说明 SDK 已经断开连接, 请开发者在界面上提示用户连接已断开, 而且正在重新建立连接
      console.log('IM => 即将重连', obj.retryCount, obj.duration)
    },
    // 断连
    onDisconnect(error) {
      // 此时说明 SDK 处于断开状态, 开发者此时应该根据错误码提示相应的错误信息, 并且跳转到登录页面
      console.log('IM => 丢失连接', error)
      const publicStore = usePublicStore()
      if (error) {
        switch (error.code) {
          // 账号或者密码错误, 请跳转到登录页面并提示错误
          case 302:
            publicStore.resetLogin({ msg: 'IM-302', type: 'im' })
            break
          // 重复登录, 已经在其它端登录了, 请跳转到登录页面并提示错误
          case 417:
            publicStore.resetLogin({ msg: 'IM-417', type: 'im' })
            break
          // 被踢, 请提示错误后跳转到登录页面
          case 'kicked':
            publicStore.resetLogin({ msg: 'IM-kicked', type: 'im' })
            break
          default:
            break
        }
      }
    },
    // 错误
    onError(error) {
      console.log('IM => error', error)
    },
    // 漫游消息
    // onRoamingMsgs(obj) {
    //   // console.log('收到漫游消息', obj)
    //   this.updateMsgs(obj.msgs)
    // },
    // 离线消息
    // onOfflineMsgs(obj) {
    //   // console.log('收到离线消息', obj)
    //   this.updateMsgs(obj.msgs)
    // },
    // 收到消息
    onMsg(msg) {
      console.log('IM => 收到消息', msg.scene, msg.type, msg)

      this.putMsg(msg)
      if (msg.sessionId === this.currSessionId) {
        this.updateCurrSessionMsgs({
          type: 'put',
          msg
        })
        // 发送已读回执
        this.sendMsgReceipt()
      }

      const callStore = useCallStore()
      callStore.setValidCallAfterUids(msg) // 设置有效通话过的用户uid

      this.filterValidUids([msg]) // 过滤有效uid
    },
    // 收到会话列表
    onSessions(sessions) {
      console.log('IM => 收到会话列表', sessions)
      // 生成用户id数组
      const accounts: string[] = []
      sessions.forEach((item: { to: string }) => {
        accounts.push(item.to)
      })
      // 变成二维数组，150
      const matrixAccounts = common.arrayToMatrix(accounts, 150) as string[]
      // 获取用户资料数组
      this.getUsers(matrixAccounts)

      this.updateSessions(sessions)

      this.filterValidUids(sessions) // 过滤有效uid
    },
    // 会话更新了
    onUpdateSession(session) {
      console.log('IM => 会话更新了', session)
      // 判断 sessionList 是否有次uid的消息了，有才赋值
      if (this.sessionList.findIndex((v) => v.to === session.to) !== -1) {
        this.latestSessionListedUid = session.to
      }
      // 获取用户信息
      if (!this.userInfos[session.to]) {
        this.getUser(session.to)
      }
      this.updateSessions(session)
    },
    // 我的个人信息
    onMyInfo(obj: NIMUserNameCard) {
      this.myInfo = obj
    },
    // 收到用户资料列表
    onUsers(users) {
      console.log('IM => 收到用户资料列表', users)
      this.updateUserInfo(users)
    },
    // 用户资料更新了
    onUpdateUser(user) {
      console.log('IM => 用户资料更新了', user)
      this.updateUserInfo(user)
    },
    // 消息已读
    sendMsgReceipt() {
      // 如果有当前会话
      const currSessionId = this.currSessionId
      if (currSessionId) {
        // 只有点对点消息才发已读回执
        if (common.parseSession(currSessionId)?.scene === 'p2p') {
          const nim = this.nim
          if (this.sessionMap[currSessionId]) {
            nim.sendMsgReceipt({
              msg: this.sessionMap[currSessionId].lastMsg,
              done: function sendMsgReceiptDone() {
                // do something
              }
            })
          }
        }
      }
    },
    // nim初始化同步完成
    onSyncdone() {
      console.log('IM => 同步完成')
      // 取消骨架屏
      this.showSkeleton = false
      // 说明在聊天列表页
      if (this.currSessionId) {
        this.setCurrSession(this.currSessionId)
      }
    },
    // 处理更新用户信息
    updateUserInfo(users) {
      const userInfos = this.userInfos
      if (!Array.isArray(users)) {
        users = [users]
      }
      this.userInfoList = this.nim.mergeUsers(this.userInfoList, users)
      users.forEach((user) => {
        const account = user.account
        if (account) {
          userInfos[account] = user
        }
      })
    },

    // 处理更新会话
    updateSessions(sessions) {
      this.allUnread = 0
      const nim = this.nim
      this.sessionList = nim.mergeSessions(this.sessionList, sessions)
      this.sessionList.sort((a, b) => {
        return b.updateTime - a.updateTime
      })
      this.sessionList.forEach((item) => {
        this.sessionMap[item.id] = item
        this.allUnread += item.unread
      })
    },
    // 设置未读数
    setAllUnread(unread: number) {
      this.allUnread = unread || 0
    },
    // 设置当前会话信息
    setCurrSession(sessionId) {
      const nim = this.nim
      if (sessionId) {
        this.updateCurrSessionId({
          type: 'init',
          sessionId
        })
        if (Object.keys(nim).length) {
          // 如果在聊天页面刷新，此时还没有nim实例，需要在onSessions里同步
          nim.setCurrSession(sessionId)
          this.updateCurrSessionMsgs({
            type: 'init',
            sessionId
          })
          // 发送已读回执
          this.sendMsgReceipt()
        }
      }
    },
    // 更新当前会话id，用于唯一判定是否在current session状态
    updateCurrSessionId(obj) {
      const type = obj.type || ''
      if (type === 'destroy') {
        this.currSessionId = ''
        this.finished = false
        this.currSessionMsgs = []
      } else if (type === 'init') {
        if (obj.sessionId && obj.sessionId !== this.currSessionId) {
          this.currSessionId = obj.sessionId
        }
      }
    },
    // 更新当前会话列表的聊天记录，包括历史消息、单聊消息等，不包括聊天室消息
    // replace: 替换idClient的消息
    updateCurrSessionMsgs(obj) {
      const type = obj.type || ''
      if (type === 'destroy') {
        // 清空会话消息
        this.currSessionMsgs = []
        this.currSessionLastMsg = {} as NIMMessage
        this.updateCurrSessionId({
          type: 'destroy'
        })
      } else if (type === 'init') {
        // 初始化会话消息列表
        if (this.currSessionId) {
          const sessionId = this.currSessionId
          // let currSessionMsgs = [].concat(this.msgs[sessionId] || [])
          // // 做消息截断
          // const limit = this.limit
          // const msgLen = currSessionMsgs.length
          // if (msgLen - limit > 0) {
          //   this.currSessionLastMsg = currSessionMsgs[msgLen - limit]
          //   currSessionMsgs = currSessionMsgs.slice(msgLen - limit, msgLen)
          // } else if (msgLen > 0) {
          //   this.currSessionLastMsg = currSessionMsgs[0]
          // } else {
          //   this.currSessionLastMsg = {} as NIMMessage
          // }
          // this.currSessionMsgs = []
          // let lastMsgTime = 0
          // currSessionMsgs.forEach((msg: NIMMessage) => {
          //   if (msg.time - lastMsgTime > 1000 * 60 * 5) {
          //     lastMsgTime = msg.time
          //     this.currSessionMsgs.push({
          //       type: 'timeTag',
          //       text: formatChatDate(msg.time, false),
          //       idClient: getUuiD()
          //     })
          //   }
          //   this.currSessionMsgs.push(msg)
          // })
          // 获取一次历史消息，漫游消息有7天时效，可能会数据量不足导致页面无法上拉加载历史消息
          const scene = common.parseSession(sessionId)?.scene as
            | 'p2p'
            | 'team'
            | 'superTeam'
          const to = common.parseSession(sessionId)?.to as string
          this.nim.getHistoryMsgs({
            scene,
            to: to,
            reverse: false,
            asc: true,
            limit: this.limit,
            done: (_error, obj) => {
              console.log('IM => ', obj.msgs)
              let currSessionMsgs = obj.msgs || []
              // 做消息截断
              const limit = this.limit
              const msgLen = currSessionMsgs.length
              if (msgLen - limit > 0) {
                this.currSessionLastMsg = currSessionMsgs[msgLen - limit]
                currSessionMsgs = currSessionMsgs.slice(msgLen - limit, msgLen)
              } else if (msgLen > 0) {
                this.currSessionLastMsg = currSessionMsgs[0]
              } else {
                this.currSessionLastMsg = {} as NIMMessage
              }
              this.currSessionMsgs = []
              let lastMsgTime = 0
              currSessionMsgs.forEach((msg: NIMMessage) => {
                if (msg.time - lastMsgTime > 1000 * 60 * 5) {
                  lastMsgTime = msg.time
                  this.currSessionMsgs.push({
                    type: 'timeTag',
                    text: common.formatChatDate(msg.time, false),
                    idClient: common.getUuiD()
                  })
                }
                this.currSessionMsgs.push(msg)
              })
            }
          })
        }
      } else if (type === 'put') {
        // 追加一条消息
        const newMsg = obj.msg
        let lastMsgTime = 0
        const lenCurrMsgs = this.currSessionMsgs.length
        if (lenCurrMsgs > 0) {
          lastMsgTime = this.currSessionMsgs[lenCurrMsgs - 1].time ?? 0
        }
        if (newMsg) {
          if (newMsg.time - lastMsgTime > 1000 * 60 * 5) {
            this.currSessionMsgs.push({
              type: 'timeTag',
              text: common.formatChatDate(newMsg.time, false),
              idClient: common.getUuiD()
            })
          }
          this.currSessionMsgs.push(newMsg)
        }
      } else if (type === 'concat') {
        // 一般用于历史消息拼接
        const currSessionMsgs: myMsgType[] = []
        let lastMsgTime = 0
        obj.msgs.forEach((msg) => {
          if (msg.time - lastMsgTime > 1000 * 60 * 5) {
            lastMsgTime = msg.time
            currSessionMsgs.push({
              type: 'timeTag',
              text: common.formatChatDate(msg.time, false),
              idClient: common.getUuiD()
            })
          }
          currSessionMsgs.push(msg)
        })
        currSessionMsgs.reverse()
        currSessionMsgs.forEach((msg) => {
          this.currSessionMsgs.unshift(msg)
        })
        if (obj.msgs[0]) {
          this.currSessionLastMsg = obj.msgs[0]
        }
        emitter.emit('concatMsgsDone')
      } else if (type === 'replace') {
        const msgLen = this.currSessionMsgs.length
        const lastMsgIndex = msgLen - 1
        if (msgLen > 0) {
          for (let i = lastMsgIndex; i >= 0; i--) {
            if (this.currSessionMsgs[i].idClient === obj.idClient) {
              this.currSessionMsgs.splice(i, 1, obj.msg)
              break
            }
          }
        }
      }
    },
    // 销毁当前会话
    resetCurrSession() {
      const nim = this.nim
      nim.resetCurrSession(this.currSessionId)
      this.updateCurrSessionId({
        type: 'destroy'
      })
    },
    // 过滤有效Uids，目前给call完主播发送消息给用户触发埋点使用
    filterValidUids(msgs) {
      const callStore = useCallStore()
      msgs.forEach((v) => {
        // 如果是会话列表v.lastMsg ,如果是单纯信息v
        const lastMsg = v.lastMsg ? v.lastMsg : v
        // 忽略服务端发送的消息和小秘书，过滤有效的用户Uid
        if (
          lastMsg.fromClientType !== 'Server' &&
          lastMsg.from != this.IM_SERVER_ID
        ) {
          // 以下代码是做给 已通话过的主播主动发信息给用户 触发埋点使用;
          // 如果是会话列表来源就要判断一下 unread 是否未读
          if (v.lastMsg && v.unread) {
            callStore.validCallAfterMessageEventLog(
              Number(lastMsg.from),
              lastMsg.time
            )
          } else {
            callStore.validCallAfterMessageEventLog(
              Number(lastMsg.from),
              lastMsg.time
            )
          }
        }
      })
    },
    // 初始化，收到离线漫游消息时调用
    updateMsgs(msgs) {
      const nim = this.nim
      const tempSessionMap = {}
      msgs.forEach((msg) => {
        const sessionId = msg.sessionId
        tempSessionMap[sessionId] = true
        if (!this.msgs[sessionId]) {
          this.msgs[sessionId] = []
        }
        // sdk会做消息去重
        this.msgs[sessionId] = nim.mergeMsgs(this.msgs[sessionId], [msg])
      })
      for (const sessionId in tempSessionMap) {
        this.msgs[sessionId].sort((a, b) => {
          if (a.time === b.time) {
            // 机器人消息，回复消息时间和提问消息时间相同，提问在前，回复在后
            if (a.type === 'robot' && b.type === 'robot') {
              if (a.content && a.content.msgOut) {
                return 1
              }
              if (b.content && b.content.msgOut) {
                return -1
              }
            }
          }
          return a.time - b.time
        })
        if (sessionId === this.currSessionId) {
          this.updateCurrSessionMsgs({
            type: 'init'
          })
        }
      }
    },
    // 更新追加消息，追加一条消息
    putMsg(msg) {
      const sessionId = msg.sessionId
      if (!this.msgs[sessionId]) {
        this.msgs[sessionId] = []
      }
      const tempMsgs = this.msgs[sessionId]
      const lastMsgIndex = tempMsgs.length - 1
      if (tempMsgs.length === 0 || msg.time >= tempMsgs[lastMsgIndex].time) {
        tempMsgs.push(msg)
      } else {
        for (let i = lastMsgIndex; i >= 0; i--) {
          const currMsg = tempMsgs[i]
          if (msg.time >= currMsg.time) {
            this.msgs[sessionId].splice(i, 0, msg)
            break
          }
        }
      }
    },
    // 发送消息前需要调接口扣费，目前消息、文件需要此接口
    async deductionSend() {
      const conut = this.freeMsgCount.freeCount // 私聊免费次数，用来判断是否刚好使用完免费次数
      const res = await apiDeductionSend({ tuid: this.currSessionUid })
      const publicStore = usePublicStore()
      const { code, data } = res
      if (code === 200) {
        this.freeMsgCount.freeCount = data.freeCount
        // 如果刚好使用完，就显示下载App弹窗；flowTip（每5次发送消息）为 true 也提示，
        if ((conut === 1 && data.freeCount === 0) || data.flowTip) {
          publicStore.setDownloadAppTipsConfig(true, 3, 'msg')
        }
        return Promise.resolve()
      }
      if (code === 2103) {
        // 余额不足显示充值弹窗
        if (this.isFirstClickRechargePopup) {
          // 首次显示余额不足显示充值弹窗
          this.isFirstClickRechargePopup = false
          publicStore.setShowRechargePopup(true)
        } else {
          // 后面都显示充值提醒&引流窗口
          publicStore.setRechargeTipsPopup(true, 2, 'popup')
        }
      }
      return Promise.reject()
    },
    // 发送消息
    sendMsg(obj) {
      const nim = this.nim
      obj = obj || {}
      const type = obj.type || ''
      const runtimeConfig = useRuntimeConfig()
      const env =
        runtimeConfig.public.appMode == 'production' ||
        runtimeConfig.public.appMode == 'release'
          ? 'prod'
          : 'test'
      if (type === 'text') {
        this.deductionSend().then(() => {
          // 发送消息
          nim.sendText({
            scene: obj.scene,
            to: obj.to,
            env,
            text: obj.text,
            done: this.onSendMsgDone,
            needMsgReceipt: obj.needMsgReceipt || false
          })
        })
      } else if (['gift', 'call', 'share'].includes(type)) {
        nim.sendCustomMsg({
          scene: obj.scene,
          to: obj.to,
          env,
          content: JSON.stringify(obj.content),
          done: this.onSendMsgDone,
          needMsgReceipt: obj.needMsgReceipt || false
        })
      }
    },
    // 发送消息回调
    onSendMsgDone(error, msg) {
      this.onMsg(msg)
    },
    // 获取历史消息
    getHistoryMsgs(obj) {
      const nim = this.nim
      if (Object.keys(nim).length) {
        const { scene, to } = obj
        let options = {
          scene,
          to,
          reverse: false,
          asc: true,
          limit: this.limit,
          done: this.getHistoryMsgsDone
        }
        if (this.currSessionLastMsg) {
          options = Object.assign(options, {
            lastMsgId: this.currSessionLastMsg.idServer,
            endTime: this.currSessionLastMsg.time
          })
        }
        this.nim.getHistoryMsgs(options)
      }
    },
    // 获取历史消息回调
    getHistoryMsgsDone(error, obj) {
      console.log('IM => ', obj.msgs)
      if (obj.msgs) {
        if (obj.msgs.length === 0) {
          this.finished = true
        } else {
          this.updateCurrSessionMsgs({
            type: 'concat',
            msgs: obj.msgs
          })
        }
      }
    },
    // 获取用户资料
    getUser(account) {
      this.nim.getUser &&
        this.nim.getUser({
          account,
          done: (error, user) => {
            if (!error) {
              this.updateUserInfo(user)
            }
          }
        })
    },
    // 获取用户资料数组
    getUsers(accounts) {
      accounts.forEach((value) => {
        this.nim.getUsers &&
          this.nim.getUsers({
            accounts: value,
            done: (error, user) => {
              if (!error) {
                this.updateUserInfo(user)
              }
            }
          })
      })
    },
    // 发送文件消息
    async sendFileMsg(obj) {
      const runtimeConfig = useRuntimeConfig()
      this.deductionSend()
        .then(() => {
          const nim = this.nim
          let { type } = obj
          const { fileInput, localImg } = obj
          if (!type && fileInput) {
            type = 'file'
            if (/\.(png|jpg|bmp|jpeg|gif|webp)$/i.test(fileInput.value)) {
              type = 'image'
            }
          }
          const env =
            runtimeConfig.public.appMode == 'production' ||
            runtimeConfig.public.appMode == 'release'
              ? 'prod'
              : 'test'
          const sendMsgReceipt = this.sendMsgReceipt

          // 自己先插入一条图片类型消息
          const msg = ref({
            time: new Date().getTime(),
            localImg: localImg,
            percentage: 0,
            flow: 'out',
            idClient: common.getUuiD(),
            type
          })
          this.putMsg(msg.value)
          this.updateCurrSessionMsgs({
            type: 'put',
            msg: msg.value
          })

          const data = Object.assign(
            {
              type,
              env,
              uploadprogress: function (obj) {
                // console.log('文件总大小: ' + obj.total + 'bytes');
                // console.log('已经上传的大小: ' + obj.loaded + 'bytes');
                // console.log('上传进度: ' + obj.percentage);
                // console.log('上传进度文本: ' + obj.percentageText);
                msg.value.percentage = obj.percentage
              },
              uploaderror: function () {
                fileInput.value = ''
                console && console.log('IM => 上传失败')
              },
              uploaddone: function () {
                fileInput.value = ''
                // console.log(error);
                // console.log(file);
              },
              beforesend: function (msg) {
                console.log('IM => ', msg)
                console.log('IM => 正在发送p2p image消息, id=' + msg.idClient)
              },
              done: () => {
                // 发送已读回执
                sendMsgReceipt()
              }
            },
            obj
          )
          nim.sendFile(data)
        })
        .catch(() => {
          const { fileInput } = obj
          fileInput.value = ''
        })
    },
    // 更新个人资料
    updateMyInfo(obj: { nick: string; avatar: string }) {
      this.nim.updateMyInfo({
        nick: obj.nick,
        avatar: obj.avatar,
        done: (error, user) => {
          if (!error) {
            this.onMyInfo(user)
          }
        }
      })
    },
    // 断开连接
    disconnect() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.nim.disconnect && this.nim.disconnect()
    },
    // 重置数据
    resetData() {
      this.sessionList = []
      this.allUnread = 0
      this.translateTexts = {}
      this.freeMsgCount = { freeCount: 0, limitLv: 10, sendPic: false }
      this.userInfos = {}
    }
  },
  persist: {
    key: 'chatStore',
    paths: ['userInfos', 'translateTexts']
  }
})
